import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../context/UserContext'
import { Calendar, Users, MapPin, Phone, Mail, CreditCard, X, CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { getImageUrl, API_CONFIG } from '../config/settings'

interface Booking {
  id: number
  booking_number: number
  room_id: number
  check_in: string
  check_out: string
  guests: number
  nights: number
  customer_name: string
  customer_phone: string
  customer_email: string
  payment_method: string
  grand_total: number
  payment_status: number
  created_at: string
  room_title: string
  room_slug: string
  room_image: string
  room_rent: number
}

const Bookings = () => {
  const { userDetails } = useUser()
  const navigate = useNavigate()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<number | null>(null)

  useEffect(() => {
    // Check if user is logged in - check localStorage first (synchronous)
    const storedUser = localStorage.getItem('firebase_auth_user')
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        if (userData && userData.email) {
          // User is logged in, fetch bookings
          fetchBookings()
          return
        }
      } catch (e) {
        console.error('Error parsing stored user:', e)
      }
    }
    
    // If no stored user, check userDetails from context (might be loading)
    if (userDetails && userDetails.email) {
      fetchBookings()
      return
    }
    
    // Only redirect if we're sure there's no user (after checking both sources)
    // Give a small delay to allow context to load
    const timeoutId = setTimeout(() => {
      const finalCheck = localStorage.getItem('firebase_auth_user') || (userDetails && userDetails.email)
      if (!finalCheck) {
        navigate('/auth/login', { replace: true })
      }
    }, 100)
    
    return () => clearTimeout(timeoutId)
  }, [userDetails, navigate])

  const fetchBookings = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get email from localStorage first (most reliable), then from userDetails
      let email = null
      const storedUser = localStorage.getItem('firebase_auth_user')
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser)
          email = userData?.email
        } catch (e) {
          console.error('Error parsing stored user:', e)
        }
      }
      
      // Fallback to userDetails if not in localStorage
      if (!email && userDetails?.email) {
        email = userDetails.email
      }

      if (!email) {
        setError('User email not found')
        setLoading(false)
        navigate('/auth/login', { replace: true })
        return
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/customer/bookings?email=${encodeURIComponent(email)}`
      )

      if (!response.ok) {
        throw new Error('Failed to fetch bookings')
      }

      const data = await response.json()
      setBookings(data.bookings || [])
    } catch (err: any) {
      console.error('Error fetching bookings:', err)
      setError(err.message || 'Failed to load bookings')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelBooking = async (bookingNumber: number) => {
    if (!confirm('Are you sure you want to cancel this booking?')) {
      return
    }

    try {
      setCancellingId(bookingNumber)
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/bookings/${bookingNumber}`,
        {
          method: 'DELETE'
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to cancel booking')
      }

      // Refresh bookings list
      await fetchBookings()
    } catch (err: any) {
      alert(err.message || 'Failed to cancel booking')
    } finally {
      setCancellingId(null)
    }
  }

  const canCancelBooking = (checkIn: string): boolean => {
    if (!checkIn) return false
    
    const checkInDate = new Date(checkIn)
    const now = new Date()
    const hoursUntilCheckIn = (checkInDate.getTime() - now.getTime()) / (1000 * 60 * 60)
    
    return hoursUntilCheckIn > 24
  }

  const getPaymentStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        )
      case 1:
        return (
          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Unpaid
          </span>
        )
      case 2:
        return (
          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Paid
          </span>
        )
      case 3:
        return (
          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold flex items-center gap-1">
            <X className="h-3 w-3" />
            Cancelled
          </span>
        )
      default:
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
            Unknown
          </span>
        )
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBookings}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Bookings</h1>
          <p className="text-gray-600">View and manage your room bookings</p>
        </div>

        {/* Bookings List */}
        {bookings.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Bookings Found</h3>
            <p className="text-gray-600 mb-6">You haven't made any bookings yet.</p>
            <button
              onClick={() => navigate('/rooms')}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-semibold"
            >
              Browse Rooms
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {bookings.map((booking) => (
              <div
                key={booking.booking_number}
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Room Image */}
                    <div className="lg:w-64 flex-shrink-0">
                      <div
                        className="w-full h-48 lg:h-full rounded-lg bg-gray-200 overflow-hidden"
                        onClick={() => navigate(`/room/${booking.room_id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {booking.room_image ? (
                          <img
                            src={getImageUrl(booking.room_image, 'rooms')}
                            alt={booking.room_title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.src = 'https://via.placeholder.com/400x300?text=Room'
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <MapPin className="h-12 w-12" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {booking.room_title || 'Room'}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2">
                            Booking # {booking.booking_number}
                          </p>
                          {getPaymentStatusBadge(booking.payment_status)}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary-600 mb-1">
                            ₹{booking.grand_total.toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {booking.nights} {booking.nights === 1 ? 'night' : 'nights'}
                          </p>
                        </div>
                      </div>

                      {/* Booking Info Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-start gap-3">
                          <Calendar className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Check-in</p>
                            <p className="text-sm text-gray-600">{formatDate(booking.check_in)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Calendar className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Check-out</p>
                            <p className="text-sm text-gray-600">{formatDate(booking.check_out)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Users className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Guests</p>
                            <p className="text-sm text-gray-600">{booking.guests} {booking.guests === 1 ? 'guest' : 'guests'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <CreditCard className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Payment Method</p>
                            <p className="text-sm text-gray-600 capitalize">{booking.payment_method}</p>
                          </div>
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="border-t border-gray-200 pt-4 mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact Information</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="h-4 w-4" />
                            <span>{booking.customer_phone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="h-4 w-4" />
                            <span>{booking.customer_email}</span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => navigate(`/room/${booking.room_id}`)}
                          className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          View Room
                        </button>
                        {booking.payment_status !== 3 && canCancelBooking(booking.check_in) && (
                          <button
                            onClick={() => handleCancelBooking(booking.booking_number)}
                            disabled={cancellingId === booking.booking_number}
                            className="px-4 py-2 text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {cancellingId === booking.booking_number ? (
                              <>
                                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                Cancelling...
                              </>
                            ) : (
                              <>
                                <X className="h-4 w-4" />
                                Cancel Booking
                              </>
                            )}
                          </button>
                        )}
                        {booking.payment_status === 3 && (
                          <span className="px-4 py-2 text-sm text-gray-500">
                            Cancelled
                          </span>
                        )}
                        {booking.payment_status !== 3 && !canCancelBooking(booking.check_in) && (
                          <span className="px-4 py-2 text-sm text-gray-500">
                            Cannot cancel (check-in within 24 hours)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Bookings

